// src/components/NewRequestModal.jsx
import { useState } from "react";
import { C, urgencyColor } from "../styles/tokens";
import { Icon } from "./UI";
import { BLOOD_GROUPS } from "../data/mockData";

const URGENCY_LEVELS = [
  { label: "Scheduled", color: "#1565C0", desc: "Within 24 hrs" },
  { label: "Urgent",    color: "#E65100", desc: "Within 6 hrs"  },
  { label: "Emergency", color: "#C62828", desc: "Immediate"      },
];

export default function NewRequestModal({ onClose, onSubmit }) {
  const [form, setForm] = useState({
    patient: "", age: "", patientId: "", doctor: "", ward: "",
    blood: "", units: 1, urgency: "Urgent", reqType: "Internal",
    notes: "", notifyDonors: true, notifyStaff: true,
  });
  const [loading, setLoading] = useState(false);
  const [done,    setDone]    = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.blood) { alert("Please select a blood group"); return; }
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1600));
    setLoading(false);
    setDone(true);
  };

  if (done) {
    return (
      <div className="overlay" onClick={onClose}>
        <div className="modal" style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
          <div style={{ width: 72, height: 72, background: "rgba(56,142,60,.15)", border: "2px solid #388E3C", borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16 , zIndex: 11, marginTop: 100}}>
            <Icon name="check" size={36} color="#4CAF50" />
          </div>
          <h3 className="syne" style={{ fontSize: 22, marginBottom: 8 }}>Request Posted!</h3>
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 20 }}>
            Blood request for <strong style={{ color: C.text }}>{form.patient}</strong> has been submitted. Nearby donors and staff have been notified.
          </p>

          {/* Summary */}
          <div className="card2" style={{ padding: 16, textAlign: "left", marginBottom: 20 }}>
            {[
              ["Patient",    form.patient],
              ["Blood Group",form.blood],
              ["Units",      `${form.units} unit${form.units > 1 ? "s" : ""}`],
              ["Urgency",    form.urgency],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: C.muted, fontSize: 13 }}>{k}</span>
                <span style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{v}</span>
              </div>
            ))}
          </div>

          <button className="btn-primary" onClick={() => onSubmit(form)}>
            Back to Requests
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 className="syne" style={{ fontSize: 20, fontWeight: 900 }}>New Blood Request</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}>
            <Icon name="close" size={20} color={C.muted} />
          </button>
        </div>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Patient Details */}
          <section>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, letterSpacing: 0.6, marginBottom: 10 }}>PATIENT DETAILS</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ gridColumn: "1/-1" }}>
                <label className="inp-label">Patient Full Name *</label>
                <div className="inp-wrap">
                  <span className="inp-icon"><Icon name="person" size={14} color={C.red} /></span>
                  <input className="inp" placeholder="Rahul Sharma" value={form.patient} onChange={(e) => upd("patient", e.target.value)} required />
                </div>
              </div>
              <div>
                <label className="inp-label">Age *</label>
                <div className="inp-wrap">
                  <span className="inp-icon"><Icon name="person" size={13} color={C.red} /></span>
                  <input className="inp" type="number" placeholder="35" value={form.age} onChange={(e) => upd("age", e.target.value)} required min={1} max={120} />
                </div>
              </div>
              <div>
                <label className="inp-label">Patient ID *</label>
                <div className="inp-wrap">
                  <span className="inp-icon"><Icon name="stats" size={13} color={C.red} /></span>
                  <input className="inp" placeholder="PAT-001" value={form.patientId} onChange={(e) => upd("patientId", e.target.value)} required />
                </div>
              </div>
              <div>
                <label className="inp-label">Doctor *</label>
                <div className="inp-wrap">
                  <span className="inp-icon"><Icon name="person" size={13} color={C.red} /></span>
                  <input className="inp" placeholder="Dr. Patel" value={form.doctor} onChange={(e) => upd("doctor", e.target.value)} required />
                </div>
              </div>
              <div>
                <label className="inp-label">Ward / Room *</label>
                <div className="inp-wrap">
                  <span className="inp-icon"><Icon name="hospital" size={13} color={C.red} /></span>
                  <input className="inp" placeholder="ICU-4" value={form.ward} onChange={(e) => upd("ward", e.target.value)} required />
                </div>
              </div>
            </div>
          </section>

          {/* Blood Requirement */}
          <section>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, letterSpacing: 0.6, marginBottom: 10 }}>BLOOD REQUIREMENT</div>
            <label className="inp-label">Select Blood Group *</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 14 }}>
              {BLOOD_GROUPS.map((g) => (
                <button
                  key={g} type="button"
                  onClick={() => upd("blood", g)}
                  style={{
                    padding: "10px 0", borderRadius: 10,
                    border: `1.5px solid ${form.blood === g ? C.primary : C.border}`,
                    background: form.blood === g ? "rgba(198,40,40,.15)" : C.card2,
                    color: form.blood === g ? C.text : C.muted,
                    fontWeight: 900, fontSize: 15, cursor: "pointer",
                    transition: "all .15s",
                    boxShadow: form.blood === g ? "0 0 0 3px rgba(198,40,40,.2)" : "none",
                  }}
                >
                  {g}
                </button>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label className="inp-label">Units Required *</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button type="button" onClick={() => upd("units", Math.max(1, form.units - 1))} style={{ width: 36, height: 36, background: "rgba(198,40,40,.1)", border: "1px solid rgba(198,40,40,.3)", borderRadius: 8, color: C.red, fontSize: 18, cursor: "pointer" }}>−</button>
                  <div style={{ flex: 1, height: 36, background: "rgba(198,40,40,.1)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ color: C.text, fontWeight: 900, fontSize: 16 }}>{form.units}</span>
                  </div>
                  <button type="button" onClick={() => upd("units", Math.min(10, form.units + 1))} style={{ width: 36, height: 36, background: "rgba(198,40,40,.1)", border: "1px solid rgba(198,40,40,.3)", borderRadius: 8, color: C.red, fontSize: 18, cursor: "pointer" }}>+</button>
                </div>
              </div>
              <div>
                <label className="inp-label">Request Type</label>
                <select className="inp" style={{ paddingLeft: 14 }} value={form.reqType} onChange={(e) => upd("reqType", e.target.value)}>
                  {["Internal", "External", "Transfer"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* Urgency */}
          <section>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, letterSpacing: 0.6, marginBottom: 10 }}>URGENCY LEVEL</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {URGENCY_LEVELS.map((u) => (
                <div
                  key={u.label}
                  onClick={() => upd("urgency", u.label)}
                  style={{
                    padding: "12px 16px", borderRadius: 12, cursor: "pointer",
                    border: `1.5px solid ${form.urgency === u.label ? u.color : C.border}`,
                    background: form.urgency === u.label ? `${u.color}12` : C.card2,
                    display: "flex", alignItems: "center", gap: 12,
                    transition: "all .15s",
                  }}
                >
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: u.color }} />
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 700, color: form.urgency === u.label ? C.text : C.muted, fontSize: 13 }}>{u.label}</span>
                    <span style={{ fontSize: 11, color: u.color, marginLeft: 8 }}>— {u.desc}</span>
                  </div>
                  <div style={{ width: 20, height: 20, borderRadius: "50%", border: `2px solid ${form.urgency === u.label ? u.color : C.border}`, background: form.urgency === u.label ? u.color : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {form.urgency === u.label && <Icon name="check" size={12} color="#fff" />}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Notes */}
          <div>
            <label className="inp-label">Additional Notes</label>
            <textarea className="inp" style={{ padding: 14, resize: "vertical", minHeight: 80 }} placeholder="Patient history, surgical notes..." value={form.notes} onChange={(e) => upd("notes", e.target.value)} />
          </div>

          {/* Notifications */}
          <div className="card2" style={{ padding: 14 }}>
            {[
              ["notifyDonors", "Notify Nearby Donors", "Alert matching donors"],
              ["notifyStaff",  "Notify Hospital Staff",  "Alert blood bank team"],
            ].map(([key, label, sub]) => (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0" }}>
                <Icon name="bell" size={15} color={C.red} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{label}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{sub}</div>
                </div>
                <label className="toggle">
                  <input type="checkbox" checked={form[key]} onChange={(e) => upd(key, e.target.checked)} />
                  <span className="toggle-slider" />
                </label>
              </div>
            ))}
          </div>

          {/* Submit */}
          <button className="btn-primary" type="submit" disabled={loading}>
            {loading
              ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}><div className="spinner" /><span>Posting request...</span></div>
              : <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><Icon name="send" size={16} color="#fff" />Post Blood Request</span>}
          </button>
        </form>
      </div>
    </div>
  );
}